import React, { useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { wiki, c, fadeIn } from './wikiStyles';
import { useWikiConfig, regeneratePage, slugToTitle } from './useWiki';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 4rem 2rem;
  text-align: center;
  animation: ${fadeIn} 0.5s ease;
`;

const Icon = styled.div`
  font-size: 3rem;
  color: ${c.accent};
  margin-bottom: 1.5rem;
  opacity: 0.7;
`;

const Title = styled.h1`
  font-family: ${wiki.fonts.heading};
  font-size: 2rem;
  font-weight: 600;
  color: ${c.text};
  margin-bottom: 1rem;
`;

const Message = styled.p`
  font-family: ${wiki.fonts.body};
  font-size: 1.1rem;
  color: ${c.textSec};
  max-width: 480px;
  line-height: 1.7;
  margin-bottom: 2rem;
`;

const BackLink = styled(Link)`
  font-family: ${wiki.fonts.ui};
  font-size: 0.9rem;
  color: ${c.accent};
  text-decoration: none;
  padding: 0.6rem 1.5rem;
  border: 1px solid ${c.accent};
  border-radius: ${wiki.radius};
  transition: all 0.25s;

  &:hover {
    background: ${c.accent};
    color: ${c.bgWhite};
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const GenerateBtn = styled.button`
  font-family: ${wiki.fonts.ui};
  font-size: 0.95rem;
  color: ${c.bgWhite};
  background: ${c.accent};
  border: none;
  padding: 0.7rem 2rem;
  border-radius: ${wiki.radius};
  cursor: pointer;
  transition: all 0.25s;
  margin-bottom: 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    opacity: 0.85;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Spinner = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const StatusMsg = styled.p`
  font-family: ${wiki.fonts.ui};
  font-size: 0.85rem;
  color: ${c.textMuted};
  max-width: 400px;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

export default function WikiNotFound() {
  const { slug } = useParams();
  const { config } = useWikiConfig();
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');

  const handleGenerate = useCallback(async () => {
    if (!slug) return;
    setGenerating(true);
    setStatusMsg('Generating article — this usually takes 5–10 minutes. Please keep this tab open.');
    try {
      await regeneratePage(config, slug);
      setStatusMsg('');
      navigate(0);
    } catch (err) {
      if (err.message === 'already_in_progress') {
        setStatusMsg('This article is already being generated. Please wait and refresh shortly.');
      } else {
        setStatusMsg(`Generation failed: ${err.message}. Please try again later.`);
      }
    } finally {
      setGenerating(false);
    }
  }, [slug, config, navigate]);

  return (
    <Wrapper>
      <Icon>◇</Icon>
      {slug ? (
        <>
          <Title>{slugToTitle(slug)}</Title>
          {!generating && !statusMsg && (
            <Message>
              This article hasn't been generated yet. You can request it now
              and our knowledge system will create it for you.
            </Message>
          )}
          {statusMsg && <StatusMsg>{statusMsg}</StatusMsg>}
          <GenerateBtn onClick={handleGenerate} disabled={generating}>
            {generating ? (
              <>
                <Spinner /> Generating…
              </>
            ) : (
              '✦ Generate this article'
            )}
          </GenerateBtn>
        </>
      ) : (
        <>
          <Title>Page Not Found</Title>
          <Message>The page you are looking for does not exist yet.</Message>
        </>
      )}
      <BackLink to="/wiki">&larr; Browse all articles</BackLink>
    </Wrapper>
  );
}
